import {
  ActionIcon,
  Checkbox,
  CloseButton,
  Popover,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { Check, ChevronDown, ChevronUp, Grid2x2 } from "lucide-react";
import { useState } from "react";

import classes from "./ad-copies.module.css";
import { sectionOf, type TextKey } from "./elements";
import type { KeyPick } from "./picks";

/** Live's tips on the placement button (`getPlacementTooltipLabel`). */
export const PLACEMENT_NEEDS_TICK = "Select a text element to edit placement";
export const PLACEMENT_INCLUDED = "Suggest placement, or keep blank for inspiration default";
/** Live's tip beside a section added from a card, while it has no placement. */
export const PLACEMENT_ADDED = "Suggest placement, or let AI decide";

export interface ElementGroupProps {
  sectionKey: TextKey;
  values: readonly string[];
  picked: KeyPick | undefined;
  /** Added from an Additional card: its tip asks for a placement. */
  added: boolean;
  onToggleAll: () => void;
  onToggleValue: (value: string) => void;
  onPlacement: (placement: string) => void;
}

/**
 * One section of a copy (live's `text-elements`, capture advanced-234/14): a round box that ticks
 * all its values or none, its icon and label that fold it, the placement, "{ticked} / {all}", then
 * a row per value that ticks.
 */
export function ElementGroup(props: ElementGroupProps) {
  const { sectionKey, values, picked, added, onToggleAll, onToggleValue, onPlacement } = props;
  const [open, setOpen] = useState(true);
  const { label, icon: Icon } = sectionOf(sectionKey);
  const ticked = picked?.values ?? [];
  const placement = picked?.placement ?? "";
  const all = values.length > 0 && ticked.length === values.length;
  const some = ticked.length > 0 && !all;
  const fold = () => setOpen((current) => !current);
  return (
    <section className={classes.group} aria-label={label}>
      <div className={classes.groupHeader}>
        <div className={classes.groupLeft}>
          <Checkbox
            radius="sm"
            aria-label={`Tick every ${label}`}
            checked={all}
            indeterminate={some}
            onChange={onToggleAll}
          />
          <UnstyledButton className={classes.groupLabel} aria-expanded={open} onClick={fold}>
            <Icon size={20} strokeWidth={1.5} aria-hidden />
            {label}
          </UnstyledButton>
        </div>
        <div className={classes.groupRight}>
          {added && placement === "" ? (
            <Text className={classes.tip}>{PLACEMENT_ADDED}</Text>
          ) : null}
          <PlacementButton
            label={label}
            placement={placement}
            disabled={ticked.length === 0}
            added={added}
            onSave={onPlacement}
          />
          <Text className={classes.count}>
            {ticked.length} / {values.length}
          </Text>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={open ? `Fold ${label}` : `Open ${label}`}
            onClick={fold}
          >
            {open ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
          </ActionIcon>
        </div>
      </div>
      {open ? (
        <div className={classes.values}>
          {values.map((value, at) => (
            <label
              key={at}
              className={classes.value}
              data-checked={ticked.includes(value) || undefined}
            >
              <Checkbox
                radius="sm"
                aria-label={value}
                checked={ticked.includes(value)}
                onChange={() => onToggleValue(value)}
              />
              <span className={classes.valueText}>{value}</span>
            </label>
          ))}
        </div>
      ) : null}
    </section>
  );
}

interface PlacementButtonProps {
  label: string;
  placement: string;
  disabled: boolean;
  added: boolean;
  onSave: (placement: string) => void;
}

/**
 * Live's placement (`text-placement`, capture advanced-234/15 to 17): greyed until a value is
 * ticked; its tip asks for one on an included section, or is the placement once set. Its popover,
 * "Placement", has an x, the text and a round check that keeps it.
 */
function PlacementButton({ label, placement, disabled, added, onSave }: PlacementButtonProps) {
  const [opened, setOpened] = useState(false);
  const [text, setText] = useState(placement);
  const tip = disabled
    ? PLACEMENT_NEEDS_TICK
    : placement !== ""
      ? placement
      : added
        ? null
        : PLACEMENT_INCLUDED;
  const button = (
    <ActionIcon
      variant={placement === "" ? "subtle" : "light"}
      aria-label={`Placement of ${label}`}
      disabled={disabled}
      onClick={() => {
        setText(placement);
        setOpened((current) => !current);
      }}
    >
      <Grid2x2 size={16} aria-hidden />
    </ActionIcon>
  );
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withinPortal>
      <Popover.Target>
        {tip === null ? (
          button
        ) : (
          <Tooltip label={tip} withArrow multiline maw={320}>
            {/* A disabled button takes no hover, so the tip hangs from its wrapper. */}
            <span>{button}</span>
          </Tooltip>
        )}
      </Popover.Target>
      <Popover.Dropdown>
        <div className={classes.placement}>
          <div className={classes.groupHeader}>
            <Text fw={700} size="sm">
              Placement
            </Text>
            <CloseButton
              size="sm"
              aria-label="Close the placement"
              onClick={() => setOpened(false)}
            />
          </div>
          <div className={classes.placementRow}>
            <Textarea
              aria-label="Placement"
              autosize
              minRows={3}
              maxRows={6}
              style={{ flex: 1 }}
              value={text}
              onChange={(event) => setText(event.currentTarget.value)}
            />
            <ActionIcon
              radius="xl"
              size="lg"
              aria-label="Keep the placement"
              onClick={() => {
                onSave(text);
                setOpened(false);
              }}
            >
              <Check size={18} aria-hidden />
            </ActionIcon>
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
